import { useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { LATERALITY_MAP, useGradeFlow } from '@/states/grade-flow';
import { PearEyeHypertensiveGrade } from '@/api/grade';
import { HypertensiveGradeScaleKey } from '@/states/grade-flow/types';

import { OptionButton } from '@/components/form/option-button';

import { DeepQuestionSection } from '../layouts-flow';

type PearEyeQuestionsProps = {
  laterality: 'left' | 'right' | 'both';
};

const LATERALITY_STYLE = {
  left: 'justify-start items-start',
  right: 'justify-end items-start',
  both: 'justify-start items-start',
};

/** We expect to grade pear eye, so here we are grouping the questions */
export function PearEyeQuestions(props: PearEyeQuestionsProps) {
  const { laterality } = props;

  const { t } = useTranslation();

  const GRADE_1_OPTIONS = useMemo(
    () =>
      [
        {
          label: t('grade-hypertensive-mild-generalized-constriction-retinal-arterioles'),
          value: 'mild_generalized_retinal_arterioles',
        },
      ] as const,
    [t]
  );

  const GRADE_2_OPTIONS = useMemo(
    () =>
      [
        {
          label: t('grade-hypertensive-definite-focal-narrowing-retinal-arterioles'),
          value: 'definite_focal_narrowing_retinal_arterioles',
        },
        { label: t('grade-hypertensive-arteriovenous-nicking'), value: 'arteriovenous_nicking' },
      ] as const,
    [t]
  );

  const GRADE_3_OPTIONS = useMemo(
    () =>
      [
        {
          label: t('grade-hypertensive-flame-shaped-hemorrhages'),
          value: 'flame_shaped_hemorrhages',
        },
        { label: t('grade-hypertensive-cotton-wool-spots'), value: 'cotton_wool_spots' },
        { label: t('grade-hypertensive-hard-exudates'), value: 'hard_exudates' },
      ] as const,
    [t]
  );

  const GRADE_4_OPTIONS = useMemo(
    () =>
      [
        { label: t('grade-hypertensive-papilledema'), value: 'papilledema' },
        { label: t('grade-hypertensive-retinal-edema'), value: 'retinal_edema' },
      ] as const,
    [t]
  );

  const { setHypertensiveQuestionScaleValue, form, isReadable } = useGradeFlow(
    useShallow((s) => ({
      isReadable: s.form.is_readable,
      form: s.form.grade_hypertensive?.[LATERALITY_MAP[laterality]],
      setHypertensiveQuestionScaleValue: s.setHypertensiveQuestionScaleValue,
    }))
  );

  const handleValueScaleChange = useCallback(
    (_value: string, scale: HypertensiveGradeScaleKey) => {
      if (!form) return;

      const key = _value as keyof PearEyeHypertensiveGrade[HypertensiveGradeScaleKey];

      // if selected deselect
      if (form[scale]?.[key]) {
        setHypertensiveQuestionScaleValue(false, { segment: scale, key }, laterality);
        return;
      }
      setHypertensiveQuestionScaleValue(true, { segment: scale, key }, laterality);
    },
    [laterality, form, setHypertensiveQuestionScaleValue]
  );

  return (
    <section className={`flex flex-col gap-4 mt-8 max-w-full ${LATERALITY_STYLE[laterality]}`}>
      <DeepQuestionSection label={t('grade-hypertensive-grade-1')}>
        {GRADE_1_OPTIONS.map((option) => (
          <OptionButton
            key={option.value}
            value={option.value}
            active={form?.grade_i?.[option.value]}
            onClick={() => handleValueScaleChange(option.value, 'grade_i')}
            disabled={!isReadable}
          >
            {option.label}
          </OptionButton>
        ))}
      </DeepQuestionSection>
      <DeepQuestionSection label={t('grade-hypertensive-grade-2')}>
        {GRADE_2_OPTIONS.map((option) => (
          <OptionButton
            key={option.value}
            value={option.value}
            active={form?.grade_ii?.[option.value]}
            onClick={() => handleValueScaleChange(option.value, 'grade_ii')}
            disabled={!isReadable}
          >
            {option.label}
          </OptionButton>
        ))}
      </DeepQuestionSection>
      <DeepQuestionSection label={t('grade-hypertensive-grade-3')}>
        {GRADE_3_OPTIONS.map((option) => (
          <OptionButton
            key={option.value}
            value={option.value}
            active={form?.grade_iii?.[option.value]}
            onClick={() => handleValueScaleChange(option.value, 'grade_iii')}
            disabled={!isReadable}
          >
            {option.label}
          </OptionButton>
        ))}
      </DeepQuestionSection>
      <DeepQuestionSection label={t('grade-hypertensive-grade-4')}>
        {GRADE_4_OPTIONS.map((option) => (
          <OptionButton
            key={option.value}
            value={option.value}
            active={form?.grade_iv?.[option.value]}
            onClick={() => handleValueScaleChange(option.value, 'grade_iv')}
            disabled={!isReadable}
          >
            {option.label}
          </OptionButton>
        ))}
      </DeepQuestionSection>
    </section>
  );
}
